import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import VerifiedOutlinedIcon from "@mui/icons-material/VerifiedOutlined";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Card,
  Checkbox,
  Typography,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { useState } from "react";
import "../../../components/Scorm/scormApi";
import { setStopRedirect } from "../../../components/Scorm/scormApi";
import useScreenSize from "../../../hooks/useScreenSize";
import { AssignmentMenuItem } from "../../../services/api/types/general/homeAssignment";
import { gray, success } from "../../../theme/themePrimitives";

const StyledAccordion = styled(Accordion)(({ theme }) => ({
  backgroundColor: `${gray[100]} !important`,
  border: "none",
  padding: "0px !important",
  "&:hover": {
    backgroundColor: `${gray[200]} !important`,
  },
  "&.Mui-disabled:hover": {
    backgroundColor: `${gray[100]} !important`,
  },
  "&.Mui-expanded": {
    backgroundColor: "#ffffff",
  },
  "& .MuiAccordionSummary-root": {
    backgroundColor: "inherit !important",
  },
}));

const StyledCard = styled(Card)(({ theme }) => ({
  backgroundColor: `${gray[100]} !important`,
  padding: "0 !important",
  borderRadius: "0px",
  boxShadow: "none",
  borderBottom: `1px solid ${gray[300]}`,
  "&:hover": {
    backgroundColor: `${gray[200]} !important`,
  },
  "&.Mui-disabled:hover": {
    backgroundColor: `${gray[100]} !important`,
  },
}));

const StyledAccordionDetails = styled(AccordionDetails)(({ theme }) => ({
  padding: "0 !important",
  backgroundColor: "#ffffff",
  "& > div:hover": {
    backgroundColor: gray[100],
  },
}));

interface CourseProgressProps {
  maxChapter: string;
  sendScormData: (key: any, value: any) => void;
  sendScormDataLMS: (key: any, value: any) => void;
  assignmentMenuItems: AssignmentMenuItem[];
}

export const CourseProgress: React.FC<CourseProgressProps> = ({
  maxChapter,
  sendScormData,
  sendScormDataLMS,
  assignmentMenuItems,
}) => {
  const { isMobile, isTablet } = useScreenSize();
  const [expandedIndex, setExpandedIndex] = useState<number | null>(null);

  // Check at call time which SCORM API is initialized, not at render time
  const getIsScorm2004 = () => window.API_1484_11?.initialized === true;

  const getLastPageNumber = (assignments: AssignmentMenuItem[]) => {
    let maxPage = 0;

    assignments.forEach((chapter: AssignmentMenuItem) => {
      maxPage = Math.max(maxPage, parseInt(chapter.PageNo));
      if (chapter.subchapters) {
        chapter.subchapters.forEach((subchapter: AssignmentMenuItem) => {
          maxPage = Math.max(maxPage, parseInt(subchapter.PageNo));
        });
      }
    });

    return maxPage;
  };

  const calculateCompletionPercentage = (currentPage: number): string => {
    const max = getLastPageNumber(assignmentMenuItems);
    if (max === 0) return "0% Complete";
    const percentage = (currentPage / max) * 100;
    return `${Math.round(percentage)}% Complete`;
  };

  const maxChapterNumber = parseInt(maxChapter, 10);
  const lastPageNumber = getLastPageNumber(assignmentMenuItems);
  const isCourseComplete =
    lastPageNumber > 0 && maxChapterNumber >= lastPageNumber;

  return (
    <Box
      sx={{
        width: "100%",
        height: "100%",
        overflowY: isMobile || isTablet ? "none" : "scroll",
        background: "#fff",
      }}
    >
      <Typography
        borderTop={isMobile || isTablet ? `1px solid ${gray[300]}` : ""}
        variant="h6"
        textAlign={"center"}
        sx={{ py: 1, borderBottom: `1px solid ${gray[300]}` }}
      >
        {calculateCompletionPercentage(parseInt(maxChapter))}
      </Typography>
      {assignmentMenuItems.map(
        (chapter: AssignmentMenuItem, chapterIndex: number) => {
          const subchapters = chapter.subchapters ?? [];
          const totalSubchapters = subchapters.length;
          const hasSubchapters = totalSubchapters > 0;
          const chapterLastPage = Math.max(
            parseInt(chapter.PageNo),
            ...subchapters.map((s: AssignmentMenuItem) => parseInt(s.PageNo)),
          );
          const chapterCompleted =
            isCourseComplete || chapterLastPage < maxChapterNumber;

          const isDisabled = parseInt(chapter.PageNo) > maxChapterNumber;

          return (
            <StyledCard key={chapterIndex}>
              <StyledAccordion
                expanded={hasSubchapters && expandedIndex === chapterIndex}
                disabled={isDisabled}
              >
                <AccordionSummary
                  sx={{ padding: "0 24px !important" }}
                  expandIcon={
                    hasSubchapters ? (
                      <Box
                        className="expand-icon"
                        onClick={(e) => {
                          e.stopPropagation();
                          setExpandedIndex(
                            expandedIndex === chapterIndex
                              ? null
                              : chapterIndex,
                          );
                        }}
                        sx={{ cursor: "pointer" }}
                      >
                        <ExpandMoreIcon />
                      </Box>
                    ) : null
                  }
                  onClick={(e) => {
                    if (!(e.target as HTMLElement).closest(".expand-icon")) {
                      setStopRedirect(true);
                      if (getIsScorm2004()) {
                        sendScormData(
                          "cmi.location",
                          `${chapter.PageNo},on,off,en`,
                        );
                      } else {
                        sendScormDataLMS(
                          "cmi.core.lesson_location",
                          `${chapter.PageNo},on,off,en`,
                        );
                      }
                    }
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: "1rem",
                    }}
                  >
                    {chapterCompleted && (
                      <VerifiedOutlinedIcon sx={{ color: success[600] }} />
                    )}
                    <Box>
                      <Typography>{chapter.Title}</Typography>
                      {hasSubchapters && (
                        <Typography>
                          (
                          {
                            subchapters.filter(
                              (s: AssignmentMenuItem) =>
                                parseInt(s.PageNo) < maxChapterNumber,
                            ).length
                          }
                          /{totalSubchapters})
                        </Typography>
                      )}
                    </Box>
                  </Box>
                </AccordionSummary>

                {hasSubchapters && (
                  <StyledAccordionDetails>
                    {subchapters.map(
                      (subchapter: AssignmentMenuItem, subIndex: number) => {
                        const isSubDisabled =
                          parseInt(subchapter.PageNo) > maxChapterNumber;

                        return (
                          <Box
                            key={subIndex}
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              padding: "8px",
                              cursor: isSubDisabled ? "default" : "pointer",
                              opacity: isSubDisabled ? 0.5 : 1,
                              backgroundColor: "#fff",
                            }}
                            onClick={() => {
                              if (!isSubDisabled) {
                                setStopRedirect(true);
                                if (getIsScorm2004()) {
                                  sendScormData(
                                    "cmi.location",
                                    `${subchapter.PageNo},on,off,en`,
                                  );
                                } else {
                                  sendScormDataLMS(
                                    "cmi.core.lesson_location",
                                    `${subchapter.PageNo},on,off,en`,
                                  );
                                }
                              }
                            }}
                          >
                            {parseInt(subchapter.PageNo) < maxChapterNumber ? (
                              <Checkbox
                                checked={
                                  parseInt(subchapter.PageNo) < maxChapterNumber
                                }
                                disabled
                                onClick={(e) => e.stopPropagation()}
                              />
                            ) : (
                              <Checkbox
                                checked={true}
                                disabled
                                onClick={(e) => e.stopPropagation()}
                                sx={{
                                  "&.Mui-checked": {
                                    color: gray[300],
                                    backgroundColor: gray[100],
                                    borderColor: gray[300],
                                    boxShadow: `none`,
                                    "&:hover": {
                                      backgroundColor: gray[100],
                                    },
                                  },
                                }}
                              />
                            )}
                            <Typography>{subchapter.Title}</Typography>
                          </Box>
                        );
                      },
                    )}
                  </StyledAccordionDetails>
                )}
              </StyledAccordion>
            </StyledCard>
          );
        },
      )}
    </Box>
  );
};
